import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ChevronDown, ChevronRight } from "lucide-react";

import api, { apiErr } from "@/lib/api";
import { Empty, Loader, PageHeader, btn } from "@/components/Page";
import { useAuth } from "@/context/AuthContext";
import { LeagueBadge } from "@/components/triumvirate/LeagueBadge";
import { TriumvirateReplayModal } from "@/components/triumvirate/TriumvirateReplayModal";
import { TriumvirateTargetingModal } from "@/components/triumvirate/TriumvirateTargetingModal";
import {
  TRIUMVIRATE_MODE,
  TriumvirateHeroPanel,
  TriumvirateSectionNav,
  RuleCard,
} from "@/components/triumvirate/TriumvirateSectionChrome";

function SavedSquadPreview({ squad }) {
  return (
    <div data-testid="triumvirate-opponents-ready-card" className="border border-amber-900/35 bg-zinc-950/70 rounded-sm p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-cinzel text-2xl text-amber-200">Your Challenge Squad</h3>
            <LeagueBadge league={TRIUMVIRATE_MODE.id} label={TRIUMVIRATE_MODE.label} testId="triumvirate-opponents-ready-badge" />
          </div>
          <p className="mt-1 text-sm text-zinc-500">This same saved squad enters the targeting modal before each challenge and then fights on the shared ladder.</p>
        </div>
        <div data-testid="triumvirate-opponents-ready-status" className={`text-xs uppercase tracking-[0.16em] ${squad?.valid ? "text-emerald-300" : "text-red-300"}`}>
          {squad?.status_label || "No squad saved"}
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="border border-zinc-800 bg-zinc-950/55 p-3 rounded-sm">
          <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Average squad score</div>
          <div data-testid="triumvirate-opponents-ready-score" className="mt-2 font-cinzel text-2xl text-amber-300 tabular-nums">
            {Number(squad?.live_score || 0).toLocaleString()}
          </div>
        </div>
        <div className="border border-zinc-800 bg-zinc-950/55 p-3 rounded-sm">
          <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Saved fighters</div>
          <div data-testid="triumvirate-opponents-ready-count" className="mt-2 font-cinzel text-2xl text-amber-300 tabular-nums">
            {(squad?.gladiators || []).length}/3
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-2" data-testid="triumvirate-opponents-ready-members">
        {(squad?.gladiators || []).length === 0 ? (
          <Empty label="Save a squad on the main Ludus Trias page first." />
        ) : (
          (squad?.gladiators || []).map((fighter) => (
            <div key={fighter.gladiator_id} data-testid={`triumvirate-opponents-ready-fighter-${fighter.gladiator_id}`} className="flex flex-wrap items-center justify-between gap-3 border border-zinc-800 bg-zinc-950/45 rounded-sm px-3 py-2">
              <div>
                <div className="text-sm text-zinc-200">{fighter.name}</div>
                <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">{fighter.cls} · {fighter.lane}</div>
              </div>
              <div className="text-xs text-amber-300 tabular-nums">{Number(fighter.score || 0).toLocaleString()}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default function TriumvirateOpponents() {
  const { user } = useAuth();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fightingUserId, setFightingUserId] = useState("");
  const [challengeSetup, setChallengeSetup] = useState(null);
  const [battleResult, setBattleResult] = useState(null);
  const [openOpponentIds, setOpenOpponentIds] = useState({});

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [squadsRes, profileRes, opponentsRes] = await Promise.all([
        api.get("/triumvirate/squads"),
        api.get("/triumvirate/profile"),
        api.get("/triumvirate/opponents"),
      ]);
      setPayload({
        squads: squadsRes.data,
        profile: profileRes.data,
        opponents: opponentsRes.data,
      });
    } catch (error) {
      toast.error(apiErr(error, "Could not load Ludus Trias opponents"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const squad = payload?.squads?.squad || payload?.squads?.squads?.overall || null;

  const challenge = async (opponentUserId, targetPreferences) => {
    setFightingUserId(opponentUserId);
    try {
      const { data } = await api.post("/triumvirate/battle", { defender_user_id: opponentUserId, target_preferences: targetPreferences });
      setBattleResult(data.match);
      setChallengeSetup(null);
      toast.success("Ludus Trias battle resolved.");
      await load(true);
    } catch (error) {
      toast.error(apiErr(error, "Ludus Trias battle failed"));
    } finally {
      setFightingUserId("");
    }
  };

  if (loading && !payload) return <Loader label="Loading opponents…" />;

  const opponents = payload?.opponents?.opponents || [];

  const toggleOpponentDetails = (opponentUserId) => {
    setOpenOpponentIds((current) => ({
      ...current,
      [opponentUserId]: !current[opponentUserId],
    }));
  };

  return (
    <div data-testid="triumvirate-opponents-page" className="space-y-6">
      <PageHeader
        title="Ludus Trias Opponents"
        subtitle="Browse the one shared opponent pool, set target priorities in a minimal pre-fight modal, and challenge nearby season ratings from the unified live ladder."
        action={
          <div className="flex flex-wrap gap-2">
            <Link data-testid="triumvirate-opponents-archive-link" to="/ludus-trias/archive" className={btn("ghost")}>
              Season Archive
            </Link>
            <button data-testid="triumvirate-opponents-refresh-button" type="button" onClick={() => load()} className={btn("ghost")}>
              Refresh
            </button>
          </div>
        }
      />

      <TriumvirateHeroPanel
        pageKey="triumvirate-opponents"
        title="Browse nearby rivals in the single live-scored Ludus Trias field."
        copy="The opponent hall now pulls from one shared ladder. Your saved squad updates live, and the browser still sorts toward season ratings nearest to yours first."
        ratings={payload?.profile?.ratings || payload?.squads?.ratings}
        careerRatings={payload?.profile?.career_ratings || payload?.squads?.career_ratings}
        season={payload?.profile?.season || payload?.opponents?.season || payload?.squads?.season}
      />

      <TriumvirateSectionNav />

      <section className="grid gap-4 lg:grid-cols-3">
        <RuleCard testId="triumvirate-opponents-rule-range" title="Targeting" body="Set target priorities for your saved squad before battle." />
        <RuleCard testId="triumvirate-opponents-rule-squad" title="Readiness" body="You must save one valid Ludus Trias squad before issuing a challenge. Changes to that squad update in real-time." />
        <RuleCard testId="triumvirate-opponents-rule-replay" title="Result flow" body="The battle will resolve in text commentary. There is currently no 3D battle option for 3v3." />
      </section>

      <section className="space-y-6">
        <SavedSquadPreview squad={squad} />

        <div data-testid="triumvirate-opponents-card" className="border border-amber-900/35 bg-zinc-950/70 rounded-sm p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-cinzel text-2xl text-amber-200">Opponent Browser</h3>
                <LeagueBadge league={TRIUMVIRATE_MODE.id} label={TRIUMVIRATE_MODE.label} testId="triumvirate-opponents-badge" />
              </div>
              <p className="mt-1 text-sm text-zinc-500">Nearby season ratings are shown first.</p>
            </div>
            <div data-testid="triumvirate-opponents-count" className="text-sm text-zinc-500">
              {opponents.length} available
            </div>
          </div>

          {!squad?.valid && (
            <div data-testid="triumvirate-opponents-warning" className="mt-4 border border-amber-800/40 bg-amber-950/20 p-3 rounded-sm text-sm text-amber-200">
              Save a valid squad before issuing challenges.
            </div>
          )}

          <div className="mt-4 space-y-4" data-testid="triumvirate-opponents-list">
            {opponents.length === 0 ? (
              <Empty label="No opponents have saved a valid squad yet." />
            ) : (
              opponents.map((opponent) => (
                <div key={opponent.user_id} data-testid={`triumvirate-opponent-${opponent.user_id}`} className="border border-zinc-800 bg-zinc-950/55 p-4 rounded-sm">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-cinzel text-lg text-amber-200">{opponent.user_name}</div>
                      <div className="mt-1 text-xs uppercase tracking-[0.16em] text-zinc-500">
                        Rating {Number(opponent.rating || 0).toLocaleString()} · {opponent.wins || 0}W / {opponent.losses || 0}L
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 border border-zinc-900 bg-zinc-950/35 rounded-sm overflow-hidden">
                    <button
                      type="button"
                      data-testid={`triumvirate-opponent-toggle-${opponent.user_id}`}
                      onClick={() => toggleOpponentDetails(opponent.user_id)}
                      className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left hover:bg-zinc-900/50 transition-colors"
                    >
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">Squad details</div>
                        <div className="mt-1 text-sm text-zinc-300">Show selected fighters</div>
                      </div>
                      {openOpponentIds[opponent.user_id] ? <ChevronDown size={16} className="text-zinc-500 shrink-0" /> : <ChevronRight size={16} className="text-zinc-500 shrink-0" />}
                    </button>

                    {openOpponentIds[opponent.user_id] && (
                      <div data-testid={`triumvirate-opponent-details-${opponent.user_id}`} className="border-t border-zinc-900 px-3 py-3 space-y-2">
                        {(opponent.gladiators || []).map((fighter) => (
                          <div key={fighter.gladiator_id} data-testid={`triumvirate-opponent-fighter-${fighter.gladiator_id}`} className="flex flex-wrap items-center justify-between gap-2 border border-zinc-900 bg-zinc-950/40 px-3 py-2 rounded-sm">
                            <div className="text-sm text-zinc-300">
                              <Link data-testid={`triumvirate-opponent-gladiator-link-${fighter.gladiator_id}`} to={`/gladiator/${fighter.gladiator_id}`} className="text-amber-200 hover:text-amber-400 underline decoration-amber-700/40 underline-offset-4 transition-colors">
                                {fighter.name}
                              </Link>
                              <span className="text-zinc-500"> · {fighter.cls}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <button
                    data-testid={`triumvirate-challenge-${opponent.user_id}`}
                    type="button"
                    onClick={() => setChallengeSetup(opponent)}
                    disabled={!squad?.valid || fightingUserId === opponent.user_id}
                    className={btn() + " mt-4"}
                  >
                    {fightingUserId === opponent.user_id ? "Fighting…" : "Challenge Squad"}
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <TriumvirateTargetingModal
        open={!!challengeSetup}
        onClose={() => setChallengeSetup(null)}
        onConfirm={(targetMap) => challenge(challengeSetup.user_id, targetMap)}
        loading={Boolean(challengeSetup && fightingUserId === challengeSetup.user_id)}
        attacker={{
          user_name: user?.name || "Your Squad",
          rank: payload?.opponents?.my_rank,
          rating: payload?.opponents?.my_rating,
          fighters: squad?.gladiators || [],
        }}
        defender={challengeSetup ? {
          user_id: challengeSetup.user_id,
          user_name: challengeSetup.user_name,
          rank: challengeSetup.rank,
          rating: challengeSetup.rating,
          fighters: challengeSetup.gladiators || [],
        } : null}
      />

      <TriumvirateReplayModal match={battleResult} open={!!battleResult} onClose={() => setBattleResult(null)} userId={user?.user_id} />
    </div>
  );
}